import React, { createContext, useContext, useState, useCallback, useMemo, useEffect, useLayoutEffect, useRef } from 'react';
import dayjs from 'dayjs';

export const MonthContext = createContext();

export const useMonth = () => {
  const context = useContext(MonthContext);
  if (!context) throw new Error('useMonth must be used within MonthProvider');
  return context;
};

/**
 * Makes `defaultMonth` (YYYY-MM) the page's month while it is mounted, unless the
 * user has explicitly picked a month. Restores the app-wide default on unmount.
 * With `force`, the page always opens on `defaultMonth`, discarding a month picked
 * elsewhere; a month picked on the page itself still sticks while it stays mounted.
 * Returns `ready`: false on the first render, before the default has been applied, so a
 * page can hold its data hooks back instead of loading the previous page's month.
 */
export const usePageDefaultMonth = (defaultMonth, { force = false } = {}) => {
  const { applyDefaultMonth } = useMonth();
  const forcedRef = useRef(false);
  const [ready, setReady] = useState(false);
  useLayoutEffect(() => {
    // Force only on mount, so a later roll-over does not undo the user's pick on this page.
    const shouldForce = force && !forcedRef.current;
    forcedRef.current = true;
    return applyDefaultMonth(defaultMonth, shouldForce);
  }, [applyDefaultMonth, defaultMonth, force]);
  useLayoutEffect(() => setReady(true), []);
  return ready;
};

export const MonthProvider = ({ children, initialMonth }) => {
  const [month, setMonth] = useState(() => {
    if (initialMonth && dayjs.isDayjs(initialMonth)) {
      return initialMonth.startOf('month');
    }
    return dayjs().startOf('month');
  });

  // True once the user picks a month; page defaults never override that choice.
  const userSelectedRef = useRef(false);
  // Month a mounted page asked for; the app-wide default must not replace it.
  const pageDefaultRef = useRef(null);
  const initialMonthRef = useRef(initialMonth);
  initialMonthRef.current = initialMonth;

  useEffect(() => {
    if (initialMonth && dayjs.isDayjs(initialMonth) && !userSelectedRef.current && !pageDefaultRef.current) {
      setMonth(prev => {
        const next = initialMonth.startOf('month');
        return prev.isSame(next, 'month') ? prev : next;
      });
    }
  }, [initialMonth]);

  const setMonthFromDayjs = useCallback((val) => {
    userSelectedRef.current = true;
    setMonth(val ? dayjs(val).startOf('month') : dayjs().startOf('month'));
  }, []);

  // Applies a page-specific default month unless the user already picked one.
  // Returns a cleanup that restores the app-wide default if they still haven't.
  const isUserSelected = useCallback(() => userSelectedRef.current, []);

  const applyDefaultMonth = useCallback((defaultMonth, force = false) => {
    if (force) userSelectedRef.current = false;
    if (userSelectedRef.current) return () => {};
    const next = dayjs(defaultMonth).startOf('month');
    pageDefaultRef.current = next;
    setMonth(prev => (prev.isSame(next, 'month') ? prev : next));
    return () => {
      pageDefaultRef.current = null;
      if (userSelectedRef.current) return;
      const base = initialMonthRef.current;
      const restore = base && dayjs.isDayjs(base) ? base.startOf('month') : dayjs().startOf('month');
      setMonth(prev => (prev.isSame(restore, 'month') ? prev : restore));
    };
  }, []);

  const startDate = useMemo(() => month.startOf('month').format('YYYY-MM-DD'), [month]);
  const endDate = useMemo(() => month.endOf('month').format('YYYY-MM-DD'), [month]);
  const periodParam = useMemo(() => `${startDate}:${endDate}`, [startDate, endDate]);

  const periodDays = useMemo(() => {
    const days = month.daysInMonth();
    if (days <= 7) return '7d';
    if (days <= 31) return '30d';
    if (days <= 93) return '90d';
    return '180d';
  }, [month]);

  const value = useMemo(
    () => ({ month, setMonth: setMonthFromDayjs, applyDefaultMonth, isUserSelected, periodParam, periodDays, startDate, endDate }),
    [month, setMonthFromDayjs, applyDefaultMonth, isUserSelected, periodParam, periodDays, startDate, endDate],
  );

  return (
    <MonthContext.Provider value={value}>
      {children}
    </MonthContext.Provider>
  );
};
